import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import AnimatedSection from './AnimatedSection';
import { Button } from './ui/button';
import { BarChart3, X } from 'lucide-react';

// Shared data - same as dark version
const CX_DATA = {
  totalResponses: 203,
  reasons: [
    { label: "Prevent line tracing errors", count: 138, pct: 68.0 },
    { label: "Reduce staff frustration", count: 137, pct: 67.5 },
    { label: "Help nurses ambulate patients safely", count: 129, pct: 63.5 },
    { label: "Save nurse time", count: 127, pct: 62.6 },
    { label: "Make patient transfers more enjoyable", count: 123, pct: 60.6 },
    { label: "Prevent trips and falls", count: 117, pct: 57.6 },
    { label: "Prevent medical errors", count: 100, pct: 49.3 }
  ],
  states: [
    { state: "CA", count: 34 }, { state: "FL", count: 16 }, { state: "TX", count: 14 },
    { state: "NY", count: 14 }, { state: "PA", count: 8 }, { state: "MN", count: 8 },
    { state: "GA", count: 8 }, { state: "VA", count: 8 }, { state: "SC", count: 8 },
    { state: "NJ", count: 7 }, { state: "OH", count: 6 }, { state: "TN", count: 6 },
    { state: "NC", count: 5 }, { state: "WA", count: 5 }, { state: "IL", count: 5 },
    { state: "CO", count: 4 }, { state: "MD", count: 4 }, { state: "IN", count: 4 },
    { state: "KY", count: 4 }, { state: "OR", count: 3 }, { state: "CT", count: 3 },
    { state: "MT", count: 2 }, { state: "ID", count: 2 }, { state: "WI", count: 2 },
    { state: "NV", count: 2 }, { state: "MA", count: 2 }, { state: "AR", count: 2 },
    { state: "IA", count: 2 }, { state: "MI", count: 1 }, { state: "MO", count: 1 }
  ],
  jobCategories: [
    { label: "Floor Nurse", count: 51 },
    { label: "Bedside/Staff Nurse", count: 37 },
    { label: "Charge Nurse", count: 30 },
    { label: "Nurse Manager", count: 25 },
    { label: "Nurse Educator", count: 16 },
    { label: "Other", count: 4 },
    { label: "CNO / C-Suite", count: 3 }
  ],
  floorAreas: [
    { label: "Intensive Care Unit (ICU)", count: 17 },
    { label: "Cardiovascular-Surgical ICU", count: 13 },
    { label: "Combined ICU/CCU", count: 9 },
    { label: "Step-Down / Progressive Care", count: 7 },
    { label: "Medical ICU (MICU)", count: 6 },
    { label: "Neuro/Neurosurgical ICU", count: 4 },
    { label: "Coronary Care Unit (CCU)", count: 4 },
    { label: "Medical-Surgical ICU", count: 3 },
    { label: "Emergency Department", count: 2 },
    { label: "Pediatric ICU (PICU)", count: 2 },
    { label: "Surgical ICU (SICU)", count: 2 },
    { label: "Trauma Unit", count: 1 },
    { label: "Telemetry", count: 1 },
    { label: "Other", count: 5 }
  ]
};

const FIPS = {
  "01": "AL", "02": "AK", "04": "AZ", "05": "AR", "06": "CA", "08": "CO", "09": "CT",
  "10": "DE", "12": "FL", "13": "GA", "15": "HI", "16": "ID", "17": "IL", "18": "IN",
  "19": "IA", "20": "KS", "21": "KY", "22": "LA", "23": "ME", "24": "MD", "25": "MA",
  "26": "MI", "27": "MN", "28": "MS", "29": "MO", "30": "MT", "31": "NE", "32": "NV",
  "33": "NH", "34": "NJ", "35": "NM", "36": "NY", "37": "NC", "38": "ND", "39": "OH",
  "40": "OK", "41": "OR", "42": "PA", "44": "RI", "45": "SC", "46": "SD", "47": "TN",
  "48": "TX", "49": "UT", "50": "VT", "51": "VA", "53": "WA", "54": "WV", "55": "WI", "56": "WY"
};

const STATE_NAMES = {
  "AL": "Alabama", "AK": "Alaska", "AZ": "Arizona", "AR": "Arkansas", "CA": "California",
  "CO": "Colorado", "CT": "Connecticut", "DE": "Delaware", "FL": "Florida", "GA": "Georgia",
  "HI": "Hawaii", "ID": "Idaho", "IL": "Illinois", "IN": "Indiana", "IA": "Iowa",
  "KS": "Kansas", "KY": "Kentucky", "LA": "Louisiana", "ME": "Maine", "MD": "Maryland",
  "MA": "Massachusetts", "MI": "Michigan", "MN": "Minnesota", "MS": "Mississippi",
  "MO": "Missouri", "MT": "Montana", "NE": "Nebraska", "NV": "Nevada", "NH": "New Hampshire",
  "NJ": "New Jersey", "NM": "New Mexico", "NY": "New York", "NC": "North Carolina",
  "ND": "North Dakota", "OH": "Ohio", "OK": "Oklahoma", "OR": "Oregon", "PA": "Pennsylvania",
  "RI": "Rhode Island", "SC": "South Carolina", "SD": "South Dakota", "TN": "Tennessee",
  "TX": "Texas", "UT": "Utah", "VT": "Vermont", "VA": "Virginia", "WA": "Washington",
  "WV": "West Virginia", "WI": "Wisconsin", "WY": "Wyoming"
};

// Light theme colors for bars/donut
const BAR_COLORS_LIGHT = ['#214140', '#1F8051', '#8BBAB8', '#214140', '#1F8051', '#8BBAB8', '#214140'];
const DONUT_COLORS_LIGHT = ['#214140', '#1F8051', '#8BBAB8', '#DFEAF0', '#3ba873', '#559694', '#b0cbc9'];

// ============================================================
// ANIMATED BAR - LIGHT
// ============================================================
const AnimatedBarLight = ({ label, value, maxValue, color, showPct, delay }) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setWidth((value / maxValue) * 100);
    }, 200 + delay);
    return () => clearTimeout(timer);
  }, [value, maxValue, delay]);

  return (
    <div className="mb-3">
      <div className="flex justify-between mb-1 text-sm">
        <span className="text-gray-700">{label}</span>
        <span className="font-semibold" style={{ color: '#214140' }}>{showPct ? `${value}%` : value}</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden bg-gray-200">
        <div
          className="h-full rounded-full"
          style={{
            width: `${width}%`,
            backgroundColor: color,
            transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
      </div>
    </div>
  );
};

// ============================================================
// DONUT CHART - LIGHT
// ============================================================
const DonutChartLight = () => {
  const svgRef = useRef(null);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    if (rendered || !svgRef.current) return;
    setRendered(true);

    const data = CX_DATA.jobCategories;
    const w = 160, r = 70, inner = 42;
    const svg = d3.select(svgRef.current);
    const pie = d3.pie().value(d => d.count).sort(null);
    const arc = d3.arc().innerRadius(inner).outerRadius(r);
    const colors = d3.scaleOrdinal().range(DONUT_COLORS_LIGHT);

    svg.append('g')
      .attr('transform', `translate(${w / 2},${w / 2})`)
      .selectAll('path')
      .data(pie(data))
      .enter()
      .append('path')
      .attr('fill', (_, i) => colors(i))
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .attr('d', arc);

    svg.append('text')
      .attr('x', w / 2).attr('y', w / 2 - 4)
      .attr('text-anchor', 'middle')
      .attr('fill', '#214140')
      .attr('font-size', 24)
      .attr('font-weight', 700)
      .text('203');

    svg.append('text')
      .attr('x', w / 2).attr('y', w / 2 + 14)
      .attr('text-anchor', 'middle')
      .attr('fill', '#6b7280')
      .attr('font-size', 10)
      .text('TOTAL');
  }, [rendered]);

  return (
    <div className="flex items-center gap-8 flex-wrap">
      <svg ref={svgRef} width={160} height={160} />
      <div className="flex-1 flex flex-col gap-2" style={{ minWidth: 180 }}>
        {CX_DATA.jobCategories.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: DONUT_COLORS_LIGHT[i] }} />
            <span className="text-gray-700 flex-1">{d.label}</span>
            <span className="font-semibold" style={{ color: '#214140' }}>{(d.count / 203 * 100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// US MAP - LIGHT
// ============================================================
const USMapLight = () => {
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    if (rendered || !svgRef.current) return;
    setRendered(true);

    const stateMap = {};
    CX_DATA.states.forEach(d => stateMap[d.state] = d.count);
    const maxCount = Math.max(...CX_DATA.states.map(d => d.count));

    // Light theme: light teal to dark teal on a light background
    const colorScale = d3.scaleSequential()
      .domain([0, maxCount])
      .interpolator(t => {
        const r = Math.round(223 - t * 190);
        const g = Math.round(234 - t * 169);
        const b = Math.round(240 - t * 176);
        return `rgb(${r},${g},${b})`;
      });

    const svg = d3.select(svgRef.current);
    const proj = d3.geoAlbersUsa().scale(1200).translate([480, 300]);
    const pathGen = d3.geoPath().projection(proj);
    const tooltip = tooltipRef.current;

    fetch('https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json')
      .then(r => r.json())
      .then(us => {
        const states = topojson.feature(us, us.objects.states);

        svg.selectAll('path.state')
          .data(states.features)
          .enter()
          .append('path')
          .attr('class', 'state')
          .attr('d', pathGen)
          .attr('fill', d => {
            const abbr = FIPS[d.id];
            const count = stateMap[abbr] || 0;
            return count > 0 ? colorScale(count) : '#f3f4f6';
          })
          .attr('stroke', '#e5e7eb')
          .attr('stroke-width', 0.8)
          .style('cursor', 'pointer')
          .on('mousemove', function (event, d) {
            const abbr = FIPS[d.id];
            const count = stateMap[abbr] || 0;
            if (tooltip) {
              tooltip.style.opacity = '1';
              tooltip.style.left = (event.clientX + 14) + 'px';
              tooltip.style.top = (event.clientY - 44) + 'px';
              tooltip.innerHTML = `<div style="font-weight:700;color:#214140">${STATE_NAMES[abbr] || abbr}</div><div style="color:#1F8051;font-size:12px">${count > 0 ? `${count} unit request${count > 1 ? 's' : ''}` : 'No data'}</div>`;
            }
            d3.select(this).attr('stroke', '#214140').attr('stroke-width', 2);
          })
          .on('mouseleave', function () {
            if (tooltip) tooltip.style.opacity = '0';
            d3.select(this).attr('stroke', '#e5e7eb').attr('stroke-width', 0.8);
          });

        svg.append('path')
          .datum(topojson.mesh(us, us.objects.states, (a, b) => a !== b))
          .attr('fill', 'none')
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 0.5)
          .attr('d', pathGen);
      })
      .catch(() => {
        svg.append('text')
          .attr('x', 480).attr('y', 300)
          .attr('text-anchor', 'middle')
          .attr('fill', '#9ca3af')
          .attr('font-size', 14)
          .text('Map requires internet connection');
      });
  }, [rendered]);

  return (
    <div className="relative">
      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: 12,
          pointerEvents: 'none',
          opacity: 0,
          transition: 'opacity 0.15s',
          zIndex: 100001,
          whiteSpace: 'nowrap',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}
      />
      <div className="rounded-xl overflow-hidden bg-gray-50">
        <svg ref={svgRef} viewBox="0 0 960 600" style={{ width: '100%', height: 'auto', display: 'block' }} />
      </div>
      <div className="flex items-center gap-2 mt-3 justify-center text-xs text-gray-500">
        <span>Fewer requests</span>
        <div className="rounded-full" style={{ width: 140, height: 8, background: 'linear-gradient(90deg, #DFEAF0, #214140)' }} />
        <span>More requests</span>
      </div>
    </div>
  );
};

// ============================================================
// DASHBOARD CARD - LIGHT
// ============================================================
const DashCardLight = ({ title, subtitle, children }) => (
  <div className="rounded-xl p-6 bg-white shadow-sm" style={{ border: '1px solid #e5e7eb' }}>
    <h3 className="text-lg font-bold mb-1" style={{ color: '#214140' }}>{title}</h3>
    <p className="text-sm text-gray-500 mb-5">{subtitle}</p>
    {children}
  </div>
);

// ============================================================
// LIGHT MODAL DASHBOARD (not full screen)
// ============================================================
const DashboardLight = ({ onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const reasonsMax = Math.max(...CX_DATA.reasons.map(d => d.pct));
  const floorMax = Math.max(...CX_DATA.floorAreas.map(d => d.count));

  return (
    <div className="fixed inset-0 flex items-start justify-center p-6 sm:p-10 lg:p-16" style={{ zIndex: 99999 }}>
      {/* Backdrop - semi-transparent so you see the site behind */}
      <div className="absolute inset-0" onClick={onClose} style={{ background: 'rgba(0,0,0,0.5)' }} />

      {/* Close button */}
      <button
        onClick={onClose}
        className="fixed top-8 right-8 sm:top-12 sm:right-14 lg:top-18 lg:right-20 rounded-full w-10 h-10 flex items-center justify-center transition-colors shadow-lg"
        style={{ zIndex: 100000, backgroundColor: '#214140', color: 'white', border: 'none' }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1F8051'}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#214140'}
      >
        <X size={18} />
      </button>

      {/* Modal content - not full screen, rounded, with visible edges */}
      <div
        className="relative w-full max-w-6xl rounded-2xl shadow-2xl overflow-y-auto"
        style={{ zIndex: 1, backgroundColor: '#f9fafb', maxHeight: 'calc(100vh - 80px)' }}
      >
        <div className="px-6 sm:px-10 py-8">

          {/* Header */}
          <header className="pb-8 mb-8" style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold mb-2" style={{ color: '#214140' }}>
                  The Last 200 Cordflex Requests
                </h1>
                <p className="text-gray-500">
                  Who's using it, and why they want it
                </p>
              </div>
              <div className="flex gap-3 flex-wrap">
                {[
                  { num: '203', label: 'Product Requests' },
                  { num: '30', label: 'States' },
                  { num: '7', label: 'Role Types' }
                ].map((pill, i) => (
                  <div key={i} className="rounded-xl px-5 py-3 text-center" style={{ backgroundColor: '#DFEAF0' }}>
                    <span className="text-2xl font-bold block" style={{ color: '#214140' }}>{pill.num}</span>
                    <span className="text-xs uppercase tracking-wide text-gray-500">{pill.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </header>

          {/* MAP */}
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#1F8051' }}>Geographic Distribution</p>
            <DashCardLight title="Requests by State" subtitle="Hover any state to see survey counts from that state.">
              <USMapLight />
            </DashCardLight>
          </div>

          {/* REASONS + ROLES */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <DashCardLight title="Why They're Requesting It" subtitle={`% of ${CX_DATA.totalResponses} requesters citing each reason (multi-select)`}>
              {CX_DATA.reasons.map((d, i) => (
                <AnimatedBarLight key={i} label={d.label} value={d.pct} maxValue={reasonsMax} color={BAR_COLORS_LIGHT[i]} showPct delay={i * 80} />
              ))}
            </DashCardLight>
            <DashCardLight title="Clinical Role Breakdown" subtitle="Who is submitting requests">
              <DonutChartLight />
            </DashCardLight>
          </div>

          {/* FLOOR TYPES + TOP STATES */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <DashCardLight title="Unit Types Requesting" subtitle="Where the Cordflex Clip is being requested">
              {CX_DATA.floorAreas.map((d, i) => (
                <AnimatedBarLight key={i} label={d.label} value={d.count} maxValue={floorMax} color={BAR_COLORS_LIGHT[i % BAR_COLORS_LIGHT.length]} showPct={false} delay={i * 60} />
              ))}
            </DashCardLight>
            <DashCardLight title="Requesters by State" subtitle="# of requests by state">
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {CX_DATA.states.map((d, i) => (
                  <div key={i} className="rounded-lg px-3 py-2 flex justify-between items-center text-sm" style={{ backgroundColor: '#DFEAF0' }}>
                    <span className="font-semibold" style={{ color: '#214140' }}>{d.state}</span>
                    <span className="font-bold" style={{ color: '#1F8051' }}>{d.count}</span>
                  </div>
                ))}
              </div>
            </DashCardLight>
          </div>

          {/* Footer */}
          <footer className="mt-12 pb-4 pt-6 text-center text-sm text-gray-400" style={{ borderTop: '1px solid #e5e7eb' }}>
            <strong style={{ color: '#214140' }}>Cordflex Clip</strong> &middot; Nursetech Medical™ Corp &middot;
            Data from the last 203 product requests, each with a completed survey
          </footer>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// PREVIEW BAR - LIGHT
// ============================================================
const PreviewBarLight = ({ label, pct, isVisible }) => (
  <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
    <span className="flex-shrink-0" style={{ width: 220 }}>{label}</span>
    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#c2d3d2' }}>
      <div
        className="h-full rounded-full"
        style={{
          backgroundColor: '#214140',
          width: isVisible ? `${pct}%` : '0%',
          transition: 'width 1.2s cubic-bezier(0.4,0,0.2,1)'
        }}
      />
    </div>
    <span className="font-semibold flex-shrink-0" style={{ color: '#214140', width: 36, textAlign: 'right' }}>
      {Math.round(pct)}%
    </span>
  </div>
);

// ============================================================
// MAIN LIGHT SECTION
// ============================================================
const SurveyInsightsLight = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [barsVisible, setBarsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setTimeout(() => setBarsVisible(true), 300);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleOpen = useCallback(() => setModalOpen(true), []);
  const handleClose = useCallback(() => setModalOpen(false), []);

  return (
    <>
      <section ref={sectionRef} className="py-20" style={{ backgroundColor: '#DFEAF0' }}>
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fade-up">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#214140' }}>
                Nurses are asking for Cordflex
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
                See the demand data behind the last <span className="font-bold" style={{ color: '#1F8051' }}>203 product requests</span> across <span className="font-bold" style={{ color: '#1F8051' }}>30 states</span>
              </p>

              {/* Preview bars */}
              <div className="hidden sm:block max-w-lg mx-auto mb-8">
                <PreviewBarLight label="Prevent line tracing errors" pct={68} isVisible={barsVisible} />
                <PreviewBarLight label="Reduce staff frustration" pct={67.5} isVisible={barsVisible} />
                <PreviewBarLight label="Help nurses ambulate patients" pct={63.5} isVisible={barsVisible} />
              </div>

              {/* Stat chips */}
              <div className="flex gap-3 sm:gap-4 justify-center flex-wrap mb-10">
                {[
                  { num: '203', label: 'Product requests' },
                  { num: '30', label: 'States represented' },
                  { num: '68%', label: 'Cite tracing errors' }
                ].map((chip, i) => (
                  <div key={i} className="rounded-lg px-3 sm:px-4 py-2 flex items-center gap-2 bg-white shadow-sm">
                    <span className="text-lg sm:text-xl font-bold" style={{ color: '#214140' }}>{chip.num}</span>
                    <span className="text-xs text-gray-500">{chip.label}</span>
                  </div>
                ))}
              </div>

              {/* CTA - pulsing button */}
              <Button
                onClick={handleOpen}
                className="cx-pulse-btn-light px-8 py-4 text-lg font-semibold hover:opacity-90 transition-opacity duration-200"
                style={{ backgroundColor: '#214140', color: 'white' }}
              >
                <BarChart3 className="h-5 w-5 mr-2" />
                Explore the Data
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Pulsing glow for light button */}
      <style>{`
        .cx-pulse-btn-light {
          animation: cxBtnGlowLight 2.5s ease-in-out infinite;
        }
        .cx-pulse-btn-light:hover {
          animation: none;
          box-shadow: 0 8px 30px rgba(33,65,64,0.3);
        }
        @keyframes cxBtnGlowLight {
          0%, 100% { box-shadow: 0 0 0 0 rgba(33,65,64,0.4); }
          50% { box-shadow: 0 0 0 14px rgba(33,65,64,0); }
        }
      `}</style>

      {/* Modal */}
      {modalOpen && <DashboardLight onClose={handleClose} />}
    </>
  );
};

export default SurveyInsightsLight;
